import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, ArrowLeft, Package } from 'lucide-react';
import zoneService from '../services/zoneService';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Zones = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await zoneService.getAllZones();
      if (response.success) {
        setZones(response.data);
      } else {
        setError('Failed to load zones');
      }
    } catch (err) {
      console.error('Error fetching zones:', err);
      setError('Failed to load zones. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Header />

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-5 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl bg-white/80 backdrop-blur-xl shadow-sm border border-white/30 hover:bg-white transition-all"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-800">Shopping Zones</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">Explore products by zones</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500 text-sm">Loading zones...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-2">⚠️</div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-1">Something went wrong</h2>
            <p className="text-sm text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchZones}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-md transition-all text-sm"
            >
              Try Again
            </button>
          </div>
        ) : zones.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-2">🏪</div>
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-1">No zones available</h2>
            <p className="text-sm text-gray-600 mb-4">Zones are being set up. Check back soon!</p>
            <button
              onClick={() => navigate('/store')}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-md transition-all text-sm"
            >
              Browse All Products
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {zones.map((zone) => (
              <Link
                key={zone._id}
                to={`/zones/${zone._id}`}
                className="group bg-white/80 backdrop-blur-xl rounded-2xl shadow-sm border border-white/30 p-4 sm:p-6 hover:shadow-lg hover:border-purple-200 transition-all duration-300 flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mb-3 sm:mb-4 text-3xl sm:text-4xl group-hover:scale-110 transition-transform duration-300">
                  {zone.icon || '📦'}
                </div>
                <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-1 group-hover:text-purple-700 transition-colors line-clamp-1">
                  {zone.name}
                </h3>
                {zone.description && (
                  <p className="text-[11px] sm:text-xs text-gray-500 mb-2 line-clamp-2">
                    {zone.description}
                  </p>
                )}
                <div className="flex items-center gap-1.5 text-[11px] sm:text-xs text-purple-600 font-medium mt-auto">
                  <Package size={14} />
                  <span>{zone.productCount || 0} Products</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Zones;

